import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

export default function TermsOfServicePage() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-white font-['Outfit',sans-serif] text-gray-900">
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');

        .text-gradient {
          background: linear-gradient(135deg, #FF6B35 0%, #FFA500 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>

            {/* Navigation */}
            <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white'}`}>
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        <Link to="/" className="flex items-center gap-2">
                            <img src="/logo-qorvix.png" alt="Qorvix Logo" className="h-10 w-auto" />
                        </Link>

                        <div className="hidden lg:flex items-center space-x-8">
                            <Link to="/" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Home</Link>
                            <Link to="/about" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">About</Link>
                            <Link to="/how-it-works" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">How it works</Link>
                            <Link to="/portfolio" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Portfolio</Link>
                            <Link to="/contact" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Contact us</Link>
                        </div>

                        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden text-gray-900">
                            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                        </button>
                    </div>
                </div>
            </nav>

            {mobileMenuOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)}></div>
                    <div className="fixed top-8 left-1/2 -translate-x-1/2 w-[90%] max-w-sm bg-white rounded-3xl shadow-2xl p-8 z-50">
                        <button onClick={() => setMobileMenuOpen(false)} className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
                            <X size={24} />
                        </button>
                        <nav className="mt-4 space-y-6">
                            <Link to="/" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Home</Link>
                            <Link to="/about" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>About</Link>
                            <Link to="/how-it-works" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>How it works</Link>
                            <Link to="/portfolio" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Portfolio</Link>
                            <Link to="/contact" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Contact us</Link>
                        </nav>
                    </div>
                </div>
            )}

            {/* Hero */}
            <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-orange-50 to-white">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-5xl md:text-6xl font-bold mb-6">
                        <span className="text-gradient">Terms of Service</span>
                    </h1>
                    <p className="text-lg text-gray-700">Last updated: April 22, 2026</p>
                </div>
            </section>

            {/* Content */}
            <section className="py-16 px-4">
                <div className="max-w-4xl mx-auto">
                    <div className="space-y-10 text-gray-700 leading-relaxed">

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
                            <p>
                                By accessing or using the services provided by Qorvix Technologies ("Qorvix", "we", "our", or "us"), you ("Client", "you", or "your") agree to be bound by these Terms of Service ("Terms"). If you do not agree with these Terms, please do not use our services.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Services</h2>
                            <p>
                                Qorvix provides AI-powered automation solutions, including but not limited to lead qualification, CRM integrations, WhatsApp automation, workflow automation, and related consulting services. Specific deliverables, timelines, and costs will be detailed in a separate Service Agreement or proposal.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Eligibility</h2>
                            <p>
                                You must be at least 18 years old and have the legal authority to enter into binding agreements on behalf of yourself or the business you represent. By using our services, you represent and warrant that you meet these requirements.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. User Responsibilities</h2>
                            <p className="mb-3">You agree to:</p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li>Provide accurate, current, and complete information when using our forms or services</li>
                                <li>Maintain the confidentiality of any account credentials provided to you</li>
                                <li>Use our services only for lawful purposes and in compliance with all applicable laws</li>
                                <li>Not attempt to interfere with, disrupt, or gain unauthorized access to our systems</li>
                                <li>Respect the intellectual property rights of Qorvix and third parties</li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Payments and Fees</h2>
                            <p>
                                Fees for our services will be specified in the applicable Service Agreement or invoice. Unless otherwise stated, all fees are due within the timeframe indicated. Late payments may be subject to interest charges, and we reserve the right to suspend services for non-payment.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Intellectual Property</h2>
                            <p>
                                All content, trademarks, logos, software, and materials on the Qorvix website and services are the property of Qorvix Technologies or its licensors and are protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our prior written consent. Custom deliverables created for clients are subject to the terms set forth in the applicable Service Agreement.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Confidentiality</h2>
                            <p>
                                Both parties agree to keep confidential any non-public information shared during the course of the engagement. This obligation survives the termination of the relationship.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Warranties and Disclaimers</h2>
                            <p>
                                Our services are provided "as is" and "as available" without warranties of any kind, either express or implied. While we strive to deliver high-quality automation systems, Qorvix does not guarantee specific business outcomes, revenue, or conversion rates. Results will vary based on your business, implementation, and market conditions.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Limitation of Liability</h2>
                            <p>
                                To the fullest extent permitted by law, Qorvix shall not be liable for any indirect, incidental, consequential, special, or punitive damages, including lost profits, arising from or related to the use of our services. Our total liability shall not exceed the amount paid by you for the services giving rise to the claim in the 12 months prior to the event.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Third-Party Services</h2>
                            <p>
                                Our services may integrate with third-party tools (such as CRM systems, messaging platforms, and analytics tools). We are not responsible for the availability, accuracy, or practices of these third parties. Your use of third-party services is governed by their respective terms and privacy policies.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Termination</h2>
                            <p>
                                Either party may terminate the engagement in accordance with the terms set out in the applicable Service Agreement. Qorvix reserves the right to suspend or terminate access to our services for breaches of these Terms, non-payment, or any conduct we deem harmful to our business or other clients.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Indemnification</h2>
                            <p>
                                You agree to indemnify and hold Qorvix harmless from any claims, losses, liabilities, damages, costs, and expenses (including reasonable legal fees) arising out of your use of our services, your breach of these Terms, or your violation of any applicable laws or third-party rights.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Governing Law</h2>
                            <p>
                                These Terms shall be governed by and interpreted in accordance with the laws of England and Wales. Any disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Changes to These Terms</h2>
                            <p>
                                We may update these Terms from time to time. The updated version will be indicated by a revised "Last updated" date. Continued use of our services after changes constitutes acceptance of the new Terms.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Contact Us</h2>
                            <p>If you have any questions about these Terms, please contact us:</p>
                            <div className="mt-4 p-6 bg-gray-50 rounded-lg">
                                <p><strong>Qorvix Technologies</strong></p>
                                <p>London, England, UK</p>
                                <p>Email: <a href="mailto:support@qorvix.ai" className="text-orange-500 hover:underline">support@qorvix.ai</a></p>
                                <p>Phone: +44 7757 233260</p>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-white py-12 px-4 border-t border-gray-200">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <h3 className="text-xl font-bold mb-4">
                                <span className="text-gradient">Qorvix</span> — Automate What Matters
                            </h3>
                            <p className="text-gray-600 text-sm leading-relaxed mb-6">
                                Qorvix helps businesses automate workflows, connect tools, and scale faster using AI-powered systems.
                            </p>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Contact</h4>
                            <div className="space-y-2 text-sm text-gray-600">
                                <p>London, England, UK</p>
                                <p>+44 7757 233260</p>
                                <p>Support@qorvix.ai</p>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Company</h4>
                            <div className="space-y-2 text-sm">
                                <Link to="/" className="block text-gray-600 hover:text-gray-900 transition-colors">Home</Link>
                                <Link to="/about" className="block text-gray-600 hover:text-gray-900 transition-colors">About</Link>
                                <Link to="/portfolio" className="block text-gray-600 hover:text-gray-900 transition-colors">Portfolio</Link>
                                <Link to="/contact" className="block text-gray-600 hover:text-gray-900 transition-colors">Contact</Link>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Legal</h4>
                            <div className="space-y-2 text-sm">
                                <Link to="/privacy-policy" className="block text-gray-600 hover:text-gray-900 transition-colors">Privacy Policy</Link>
                                <Link to="/terms-of-service" className="block text-gray-600 hover:text-gray-900 transition-colors">Terms of Service</Link>
                            </div>
                        </div>
                    </div>

                    <div className="pt-8 flex items-center justify-between border-t border-gray-200">
                        <div className="text-2xl font-bold text-gradient">Qorvix</div>
                        <div className="text-sm text-gray-600">
                            © Qorvix Technologies. All Rights Reserved.
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
